
import { Farm } from '@/types/database.types';

export const validateFarmRow = (row: any, index: number): string[] => {
  const errors: string[] = [];
  
  if (!row.region) {
    errors.push(`Row ${index + 2}: Missing required field 'region'`);
  }
  
  if (!row.farm_size && row.farm_size !== 0) {
    errors.push(`Row ${index + 2}: Missing required field 'farm_size'`);
  }
  
  if (!row.farmer_name) {
    errors.push(`Row ${index + 2}: Missing required field 'farmer_name'`);
  }
  
  if (!row.phone_number) {
    errors.push(`Row ${index + 2}: Missing required field 'phone_number'`);
  }
  
  if (!row.product_name) {
    errors.push(`Row ${index + 2}: Missing required field 'product_name'`);
  }
  
  return errors;
};

export const normalizeRowFields = (row: any): Record<string, any> => {
  const normalizedRow: any = {};
  
  // Enhanced normalization: lowercase, remove special chars, spaces to underscores
  const normalizeKey = (key: string): string => {
    return key
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove special characters except word chars and spaces
      .replace(/\s+/g, '_')     // Replace spaces with underscores
      .replace(/_+/g, '_')      // Replace multiple underscores with single
      .replace(/^_|_$/g, '');   // Remove leading/trailing underscores
  };
  
  // Create normalized key mapping
  Object.keys(row).forEach(key => {
    const normalizedKey = normalizeKey(key);
    normalizedRow[normalizedKey] = row[key];
  });
  
  // Function to find value by multiple possible keys
  const findByKeys = (keys: string[]): any => {
    for (const key of keys) {
      const normalizedKey = normalizeKey(key);
      if (normalizedRow[normalizedKey] !== undefined && normalizedRow[normalizedKey] !== null) {
        return normalizedRow[normalizedKey];
      }
    }
    return null;
  };
  
  // Function to normalize string values
  const normalizeString = (value: any): string | null => {
    if (value === null || value === undefined || value === '') return null;
    return String(value).trim();
  };
  
  // Function to normalize product names
  const normalizeProductName = (value: any): string | null => {
    if (!value) return null;
    return String(value)
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  };
  
  // Function to normalize region names
  const normalizeRegion = (value: any): string | null => {
    if (!value) return null;
    return String(value)
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  };

  // Function to normalize boolean values
  const normalizeBoolean = (value: any): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const str = value.toLowerCase().trim();
      return str === 'yes' || str === 'true' || str === '1' || str === 'y';
    }
    return Boolean(value);
  };

  return {
    region: normalizeRegion(findByKeys([
      'region', 'area', 'location', 'state', 'province'
    ])),
    farm_size: findByKeys([
      'farm_size', 'farm size', 'land_size', 'land size', 'size', 'acreage', 'hectares'
    ]),
    irrigation_type: normalizeString(findByKeys([
      'irrigation_type', 'irrigation type', 'irrigation', 'water_source', 'water source'
    ])),
    latitude: findByKeys([
      'latitude', 'lat', 'latitude_coordinate', 'lat_coord'
    ]),
    longitude: findByKeys([
      'longitude', 'long', 'lng', 'longitude_coordinate', 'long_coord'
    ]),
    farmer_name: normalizeString(findByKeys([
      'farmer_name', 'farmer name', 'name', 'farmer', 'owner_name', 'owner name'
    ])),
    phone_number: normalizeString(findByKeys([
      'phone_number', 'phone number', 'phone', 'contact', 'mobile', 'telephone', 'tel'
    ])),
    union_name: normalizeString(findByKeys([
      'union_name', 'union name', 'union', 'cooperative', 'coop'
    ])),
    has_contract: normalizeBoolean(findByKeys([
      'has_contract', 'has contract', 'contract', 'contract_status', 'contract status'
    ])),
    has_phone_access: normalizeBoolean(findByKeys([
      'has_phone_access', 'has phone access', 'phone_access', 'phone access'
    ])),
    officer: normalizeString(findByKeys([
      'officer', 'field_officer', 'field officer', 'agent', 'extension_officer', 'extension officer'
    ])),
    product_name: normalizeProductName(findByKeys([
      'product_name', 'product name', 'crop', 'product', 'commodity', 'produce'
    ])),
    distance_from_addis: findByKeys([
      'distance_from_addis', 'distance from addis', 'distance', 'distance_km', 'distance km'
    ]),
    city: normalizeString(findByKeys([
      'city', 'town', 'municipality'
    ])),
    area: normalizeString(findByKeys([
      'area', 'district', 'zone', 'sub_region', 'sub region'
    ])),
    harvest_start_date: findByKeys([
      'harvest_start_date', 'harvest start date', 'starting_date', 'starting date', 
      'start_date', 'start date', 'harvest_begin', 'harvest begin'
    ]),
    harvest_end_date: findByKeys([
      'harvest_end_date', 'harvest end date', 'ending_date', 'ending date',
      'end_date', 'end date', 'harvest_finish', 'harvest finish'
    ]),
    kebelle: normalizeString(findByKeys([
      'kebelle', 'kebele', 'locality', 'ward'
    ])),
    estimated_harvest_kg: findByKeys([
      'estimated_harvest_kg', 'estimated harvest kg', 'estimated_harvest', 'estimated harvest',
      'estimated_harvest_kg', 'estimated harvest (kg)', 'estimated_yield', 'estimated yield',
      'yield', 'harvest_estimate', 'harvest estimate', 'expected_harvest', 'expected harvest'
    ])
  };
};
